import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';

import { buildLabelledPlate, disposePlate } from './LabelledPlate';
import { TrayWheel, type TrayWheelItem } from './TrayWheel';

/**
 * The one carousel this project shows a set of labelled plates on — extracted out of
 * `PaletteUI`'s own shape-category wheel (plan/tray-categories.md), which was, until this
 * extraction, the only place the "fixed arc of positions, front item highlighted, flanks fall
 * away in scale and alpha" presentation existed. The owner, after a headset session: "we need
 * to standardize on the carousel menu we have for shape categories... Make sure to reuse the
 * label style from the shape selector, that should be the way we do it everywhere. It's
 * probably best to extract a common component there so it doesn't drift."
 *
 * This class owns exactly the part of the old `PaletteUI.rebuildWheelMeshes`/`paintWheel` pair
 * that was never actually specific to the tray's own category wheel: one `Mesh` per item, built
 * through `buildLabelledPlate` (`LabelledPlate.ts`, itself extracted out of `PaletteUI.ts` in the
 * same change, for the identical "no second hand-rolled copy" reason), positioned and scaled
 * every tick from `TrayWheel.slots()`. `TrayWheel` itself already had no Babylon dependency
 * (its own module doc: "deliberately Babylon-free, the same separation `WhiteboardLayout.ts`
 * keeps from `SceneRoot.ts`") — this class is the renderer half that was missing a name of its
 * own, wrapping one `TrayWheel` instance rather than replacing it.
 *
 * **What stayed behind in `PaletteUI.ts`:** off-hand stick navigation (`stepCursor`/`tickFire`),
 * the desktop pointer's hover/click/scroll handling, and the dwell/pick mechanics that decide
 * *when* a wheel item is chosen. Those are how the *tray's* wheel is driven — a `Carousel` only
 * ever answers "where does each item sit and how does it look right now," the same "gesture
 * mechanics stay in the class that owns the gesture" split `ModeDefinition.ts`'s own module doc
 * draws for a mode's controls. `ModeRingRenderer`'s mode selector and `PaletteUI`'s own draw-mode
 * colour readout both drive this class by calling `goTo`/`snapTo` from outside, with no stick or
 * pointer input of their own at all — see each file's own doc for why.
 *
 * **`goTo` vs `snapTo`.** `TrayWheel`'s own visual ease (`tick`) is what makes the tray's wheel
 * spin into place under a stick flick rather than snapping — appropriate there, because the
 * flick *is* the gesture, happening on this same wheel. `ModeRingRenderer` and the colour readout
 * are both mirrors of a decision that already happened somewhere else (the mode-switch flick, the
 * dominant stick's own colour axis) — easing their own copy of the wheel toward it on top of
 * that would read as lag on the very same gesture, not a second, independent motion worth
 * animating. `snapTo` sets the wheel's visual position directly to the target item, with no ease,
 * for exactly that case; `goTo` (paired with calling `tick` every frame) is for a carousel that
 * is itself the thing being driven, like the tray's own.
 */

export interface CarouselOptions {
  readonly itemWidth: number;
  readonly itemHeight: number;
  readonly ink: string;

  /** Mesh names are `${meshNamePrefix}-${item.key}` — kept a caller-supplied prefix, not a fixed
   *  one, so each of this class's three consumers keeps its own pre-existing naming scheme
   *  (`wheel-*` for the tray's category wheel, `mode-ring-*` for the mode selector,
   *  `tray-readout-swatch-*` for the colour readout) rather than all three converging on one
   *  name and breaking whichever tests or headset-debugging habits already key off the old ones. */
  readonly meshNamePrefix: string;
}

/** How much bigger, and how far forward, the wheel's own front item reads — the "this is the
 *  one" convention every highlighted tray element in this project shares
 *  (`PaletteUI.HIGHLIGHT_SCALE`/`HIGHLIGHT_FORWARD`), reused here rather than redefined, since a
 *  wheel's front item has no separate "about to be chosen" state distinct from "currently
 *  chosen" the way a grid slot's hover does. */
export const CAROUSEL_HIGHLIGHT_SCALE = 1.3;
export const CAROUSEL_HIGHLIGHT_FORWARD = -0.025;

/** Scale/alpha at 0, 1 and 2 steps from the wheel's front — plan/tray-categories.md: "Flanks
 *  fall away: scale 1.0 at +-30 degrees and 0.85 at +-60 degrees... alpha about 0.6 and 0.3." */
const SCALE_ANCHORS: readonly [number, number][] = [
  [0, CAROUSEL_HIGHLIGHT_SCALE],
  [1, 1],
  [2, 0.85],
];
const ALPHA_ANCHORS: readonly [number, number][] = [
  [0, 1],
  [1, 0.6],
  [2, 0.3],
];

/** Piecewise-linear interpolation between `[x, y]` anchor pairs, clamped past the last one. */
function interpolateAnchors(x: number, anchors: readonly [number, number][]): number {
  const lastAnchor = anchors[anchors.length - 1]!;
  const clamped = Math.min(x, lastAnchor[0]);

  for (let i = 0; i < anchors.length - 1; i += 1) {
    const [x0, y0] = anchors[i]!;
    const [x1, y1] = anchors[i + 1]!;

    if (clamped <= x1) {
      const t = x1 === x0 ? 0 : (clamped - x0) / (x1 - x0);
      return y0 + (y1 - y0) * t;
    }
  }

  return lastAnchor[1];
}

export class Carousel {
  private readonly wheel = new TrayWheel();
  private readonly meshes = new Map<string, Mesh>();

  /** The last value `setPickable` was called with — see that method's own doc for why this is
   *  remembered rather than only applied at call time. `false` matches the same default every
   *  fresh mesh used to get unconditionally. */
  private pickable = false;

  constructor(
    private readonly scene: Scene,
    private readonly parent: TransformNode,
    private readonly options: CarouselOptions,
  ) {}

  get items(): readonly TrayWheelItem[] {
    return this.wheel.items;
  }

  get activeItem(): TrayWheelItem | null {
    return this.wheel.activeItem;
  }

  get activeIndex(): number {
    return this.wheel.activeIndex;
  }

  /** Replaces the item set, rebuilding one mesh per item — a wholesale content change, the same
   *  "no ease: there is nothing to spin from" rule `TrayWheel.setItems` already documents. */
  setItems(items: readonly TrayWheelItem[], preserveKey?: string): void {
    this.wheel.setItems(items, preserveKey);
    this.rebuildMeshes();
  }

  /** Steps the active item by `delta` (typically +-1), wrapping — for a carousel driven by its
   *  own stick/scroll input, paired with `tick()` every frame to ease the visual toward it. */
  stepBy(delta: number): boolean {
    return this.wheel.stepBy(delta);
  }

  /** Rotates to the item named `key` — paired with `tick()` every frame so the visual eases
   *  toward it, the same as `stepBy`. */
  goTo(key: string): boolean {
    return this.wheel.goTo(key);
  }

  /** Rotates to the item named `key` **with no ease** — see this class's own doc for why a
   *  carousel that only ever mirrors a decision made elsewhere (the mode ring, the colour
   *  readout) snaps instead of spinning. A no-op, harmlessly, for an unknown key or one already
   *  active. Repaints immediately, so a caller needs no separate `tick()` call afterward. */
  snapTo(key: string): void {
    const index = this.wheel.items.findIndex((item) => item.key === key);

    if (index < 0) {
      return;
    }

    this.wheel.setVisualPosition(index);
    this.paint();
  }

  slots(): ReturnType<TrayWheel['slots']> {
    return this.wheel.slots();
  }

  meshFor(key: string): Mesh | undefined {
    return this.meshes.get(key);
  }

  /** Eases the visual position toward the active item and repaints every visible mesh — call
   *  every tick this carousel is shown and being driven by `stepBy`/`goTo`. Not needed after
   *  `snapTo`, which repaints immediately on its own. */
  tick(dtMs: number): void {
    this.wheel.tick(dtMs);
    this.paint();
  }

  /**
   * The single owner of every mesh this carousel currently holds' own `isPickable` flag —
   * stored (`pickable`, below) rather than merely applied once, because `rebuildMeshes` throws
   * away and rebuilds every mesh on every `setItems` call, including ones that happen live
   * while the tray is showing (`updateReadoutSwatches` -> `swatchCarousel.setItems`, called
   * every tick `readout` context is up). A caller that turned pickability on before any of
   * that had nowhere to re-apply it *to* without this: `rebuildMeshes` used to hardcode every
   * fresh mesh's `isPickable` to `false` regardless, silently reverting whatever this method
   * had set moments earlier. `PaletteUI.setPickable` used to bypass this method entirely and
   * reach into this carousel's own meshes directly (`allMeshes()`, still exposed for whatever
   * else might need to enumerate them) — two owners for one flag was the actual defect; this
   * method, applied on every rebuild rather than only at call time, is the fix.
   */
  setPickable(pickable: boolean): void {
    this.pickable = pickable;

    for (const mesh of this.meshes.values()) {
      mesh.isPickable = pickable;
    }
  }

  *allMeshes(): Generator<Mesh> {
    yield* this.meshes.values();
  }

  private rebuildMeshes(): void {
    for (const mesh of this.meshes.values()) {
      disposePlate(mesh);
    }

    this.meshes.clear();

    for (const item of this.wheel.items) {
      const mesh = buildLabelledPlate(
        this.scene,
        `${this.options.meshNamePrefix}-${item.key}`,
        item.label,
        item.color,
        this.options.ink,
        this.options.itemWidth,
        this.options.itemHeight,
      );

      mesh.parent = this.parent;
      mesh.isPickable = this.pickable;

      // Individually pickable-in-spirit via this marker, not via `isPickable` itself — the same
      // convention every other tray mesh already uses (`PaletteUI`'s own slots and action rows).
      mesh.metadata = { trayPick: true };

      this.meshes.set(item.key, mesh);
    }

    this.paint();
  }

  /**
   * Positions, yaws, scales and fades every visible item from `wheel.slots()` — the *only* place
   * a carousel's visuals are painted, unconditionally, every call, independent of any cursor
   * focus: the front item is always "the one," so there is nothing to preview on top of that.
   * Yawing each item by its own angle (not merely offsetting it in depth) is what makes the set
   * read as a wheel rather than a stack of coplanar cards — plan/tray-categories.md's own "the
   * -Z rule applies, and applies unusually loudly here," since every plate built through
   * `buildLabelledPlate` is double-sided with `backUVs` flipped and a sign error would mirror
   * every label at once.
   */
  private paint(): void {
    const visible = new Set<string>();

    for (const slot of this.wheel.slots()) {
      const mesh = this.meshes.get(slot.item.key);

      if (!mesh) {
        continue;
      }

      visible.add(slot.item.key);

      const absSteps = Math.abs(slot.stepsFromFront);
      const scale = interpolateAnchors(absSteps, SCALE_ANCHORS);
      const alpha = interpolateAnchors(absSteps, ALPHA_ANCHORS);
      const forwardBump = CAROUSEL_HIGHLIGHT_FORWARD * Math.max(0, 1 - absSteps);

      mesh.setEnabled(true);
      mesh.position.set(slot.x, 0, slot.z + forwardBump);
      (mesh.rotationQuaternion ??= Quaternion.Identity()).copyFrom(
        Quaternion.FromEulerAngles(0, slot.angleRadians, 0),
      );
      mesh.scaling.setAll(scale);

      if (mesh.material instanceof StandardMaterial) {
        mesh.material.alpha = alpha;
      }
    }

    for (const [key, mesh] of this.meshes) {
      if (!visible.has(key)) {
        mesh.setEnabled(false);
      }
    }
  }

  dispose(): void {
    for (const mesh of this.meshes.values()) {
      disposePlate(mesh);
    }

    this.meshes.clear();
  }
}
